import axiosInstance from "../lib/axios.instance";
import {
  Chat,
  CreateChatPayload,
  UpdateChatPayload,
  ResearchChatData,
  ResearchChatMessage,
  SendMessagePayload,
  SendMessageResponse,
} from "../types/research.types";

const RESEARCH_SEND_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * The chat list endpoint returns one of two shapes.
 *
 * It used to return a plain array. In June 2026 the backend started grouping
 * chats into the user's own and those shared with them. The add-in still
 * assumed an array, which broke it two ways at once: the list rendered as
 * empty ("No chats available yet") even for users who had chats, and selecting
 * a chat then called .find() on a plain object, throwing
 * `chats.find is not a function` and taking the whole task pane down with it.
 *
 * Both shapes are accepted here so the add-in works against either backend.
 */
type ListChatsResponse = Chat[] | { own_chats?: Chat[]; shared_chats?: Chat[] };

/** A chat we can actually render: it must at least have an id we can match on. */
const isUsableChat = (value: unknown): value is Chat =>
  value != null && typeof value === "object" && typeof (value as Chat).chat_id === "string";

/**
 * Flatten whatever the chat list endpoint returned into a plain array of usable
 * chats.
 *
 * Validates the groups AND the records inside them. A null entry or a record
 * without an id would otherwise survive normalisation and throw later, when
 * something reads `chat.chat_id` — which is the same class of failure that
 * blanked the task pane in the first place.
 */
export const normaliseChatList = (data: unknown): Chat[] => {
  if (Array.isArray(data)) return data.filter(isUsableChat);
  if (data == null || typeof data !== "object") return [];

  const grouped = data as { own_chats?: unknown; shared_chats?: unknown };
  const own: unknown[] = Array.isArray(grouped.own_chats) ? grouped.own_chats : [];
  const shared: unknown[] = Array.isArray(grouped.shared_chats) ? grouped.shared_chats : [];
  return [...own, ...shared].filter(isUsableChat);
};

const asOptionalString = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  return null;
};

const asStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

/**
 * get_chat has returned null `response` / missing `question` on pending rows.
 * Downstream `.trim()` on those fields took the chat pane down.
 */
export const normaliseChatMessages = (data: unknown): ResearchChatMessage[] => {
  if (!Array.isArray(data)) return [];
  return data.flatMap((raw) => {
    if (raw == null || typeof raw !== "object") return [];
    const item = raw as Record<string, unknown>;
    return [
      {
        question: typeof item.question === "string" ? item.question : "",
        response: typeof item.response === "string" ? item.response : "",
        creation_date: typeof item.creation_date === "string" ? item.creation_date : "",
        related_questions: asStringList(item.related_questions),
        status: asOptionalString(item.status),
        query_id: asOptionalString(item.query_id),
      },
    ];
  });
};

export const researchService = {
  // Get all chats for the current user (optionally filtered by document)
  getChats: async (documentId?: string): Promise<Chat[]> => {
    const params = documentId != null ? { document_id: documentId } : {};
    const response = await axiosInstance.get<ListChatsResponse>("/openai-research/list_chats", {
      params,
    });
    return normaliseChatList(response.data);
  },

  // Get a single chat by ID with messages
  getChat: async (chatId: string): Promise<ResearchChatData> => {
    const response = await axiosInstance.post<ResearchChatData>("/openai-research/get_chat", {
      chat_id: chatId,
    });
    return { prev_chat: normaliseChatMessages(response.data?.prev_chat) };
  },

  // Create a new chat
  createChat: async (payload: CreateChatPayload): Promise<Chat> => {
    const response = await axiosInstance.post<Chat>("/openai-research/create_chat", payload);
    return response.data;
  },

  // Update chat title (rename chat)
  updateChat: async (payload: UpdateChatPayload): Promise<Chat> => {
    const response = await axiosInstance.put<Chat>("/openai-research/rename_chat", payload);
    return response.data;
  },

  // Delete a chat
  deleteChat: async (chatId: string): Promise<void> => {
    await axiosInstance.delete("/openai-research/delete_chat", {
      data: {
        chat_id: chatId,
      },
    });
  },

  // Send a message in a research chat
  sendMessage: async (payload: SendMessagePayload): Promise<SendMessageResponse> => {
    const response = await axiosInstance.post<SendMessageResponse>("/openai-research/", payload, {
      // Background dispatch returns a pending envelope after ~30s; the sync
      // path can run to the ~150s research budget. A shorter timeout looked
      // like "no response", then the saved turn showed up above the next prompt.
      timeout: RESEARCH_SEND_TIMEOUT_MS,
    });
    return response.data;
  },
};
